import React, { useState, useEffect } from "react";
import { Col, Form, Input, Row, Button, Select, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import Layout from "../../components/layout";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LockOutlined,
  UserAddOutlined,
  SafetyOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { Title, Text } = Typography;

/* ─── Tokens (shared with StaffRegister) ─────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("add-patient-styles")) return;
  const el = document.createElement("style");
  el.id = "add-patient-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .ap-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .ap-wrap { max-width: 700px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .ap-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .ap-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .ap-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .ap-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* Card section */
    .ap-section {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 28px 28px 20px; margin-bottom: 20px;
    }
    .ap-section-head {
      display: flex; align-items: center; gap: 10px; margin-bottom: 24px;
      padding-bottom: 16px; border-bottom: 1.5px solid #f1f5f9;
    }
    .ap-section-icon {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }

    /* Form spacing */
    .ap-root .ant-form-item { margin-bottom: 20px !important; }
    .ap-root .ant-form-item-label { padding-bottom: 4px !important; line-height: 1 !important; }
    .ap-root .ant-form-item-label > label {
      font-size: 12px !important; font-weight: 600 !important;
      color: ${SLATE_MID} !important; letter-spacing: .2px;
      height: 20px !important; line-height: 20px !important;
    }
    .ap-root .ant-form-item-explain { min-height: 0 !important; font-size: 11px !important; }

    /* Plain input */
    .ap-root .ant-input {
      border-radius: 10px !important; height: 44px !important; line-height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .ap-root .ant-input:hover, .ap-root .ant-input:focus {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Input with prefix/suffix */
    .ap-root .ant-input-affix-wrapper {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .ap-root .ant-input-affix-wrapper .ant-input {
      height: 100% !important; border: none !important; box-shadow: none !important;
      padding: 0 !important; background: transparent !important;
    }
    .ap-root .ant-input-affix-wrapper:hover,
    .ap-root .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .ap-root .ant-input-password { padding-right: 12px !important; }

    /* Select */
    .ap-root .ant-select-selector {
      border-radius: 10px !important; height: 44px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 12px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      box-sizing: border-box !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .ap-root .ant-select-selection-item,
    .ap-root .ant-select-selection-placeholder { line-height: 42px !important; }
    .ap-root .ant-select-focused .ant-select-selector,
    .ap-root .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Verified badge */
    .ap-verified-badge {
      display: inline-flex; align-items: center; gap: 6px;
      background: #f0fdf4; border: 1.5px solid #bbf7d0;
      border-radius: 10px; padding: 10px 14px;
      font-size: 12px; font-weight: 600; color: #15803d;
      font-family: 'DM Sans', sans-serif;
    }
    .ap-verified-dot {
      width: 8px; height: 8px; border-radius: 50%; background: #22c55e; flex-shrink: 0;
      box-shadow: 0 0 0 3px #bbf7d0;
    }

    /* Hint box */
    .ap-hint {
      background: ${TEAL_LIGHT}; border-radius: 12px; padding: 12px 16px;
      display: flex; align-items: flex-start; gap: 10px; margin-bottom: 24px;
    }
    .ap-hint-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: ${EMERALD}; flex-shrink: 0; margin-top: 5px;
    }

    /* Submit */
    .ap-submit {
      height: 52px !important; width: 100% !important;
      border-radius: 14px !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      box-shadow: 0 8px 20px ${TEAL}44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .ap-submit:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 14px 32px ${TEAL}55 !important;
    }

    /* Animations */
    @keyframes apFadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .ap-fade   { animation: apFadeUp .5s ease both; }
    .ap-fade-1 { animation-delay: .05s; }
    .ap-fade-2 { animation-delay: .12s; }
    .ap-fade-3 { animation-delay: .20s; }
  `;
  document.head.appendChild(el);
};

/* ─── Section wrapper ─────────────────────────────────────────── */
function Section({ icon, title, children, className = "" }) {
  return (
    <div className={`ap-section ${className}`}>
      <div className="ap-section-head">
        <div className="ap-section-icon">{icon}</div>
        <Title
          level={5}
          style={{
            margin: 0,
            fontFamily: "'Sora', sans-serif",
            color: SLATE,
            fontWeight: 700,
          }}
        >
          {title}
        </Title>
      </div>
      {children}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
function AddPatient() {
  const [locations, setLocations] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    axios
      .get("/api/user/get-all-location", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        if (res.data.success) setLocations(res.data.data);
      })
      .catch(console.error);
  }, []);

  const onFinish = async (values) => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/admin/register-patient",
        {
          name: values.name,
          email: values.email,
          phone: values.phone,
          password: values.password,
          location: values.location,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        navigate("/admin/add-patient"); // adjust to your patients list route
      } else {
        toast.error(res.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    }
  };

  return (
    <Layout>
      <div className="ap-root">
        <div className="ap-wrap">

          {/* ── Hero ── */}
          <div className="ap-hero ap-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="ap-eyebrow">
                <MedicineBoxOutlined style={{ marginRight: 5 }} />
                Patient Management
              </div>
              <Title
                level={2}
                style={{
                  margin: "6px 0 4px",
                  fontFamily: "'Sora', sans-serif",
                  color: WHITE,
                  fontWeight: 800,
                }}
              >
                Add New Patient
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Register a patient account directly — no OTP required
              </Text>
            </div>
          </div>

          <Form layout="vertical" onFinish={onFinish} requiredMark={false}>

            {/* ── Section 1: Personal Info ── */}
            <Section
              icon={<UserOutlined />}
              title="Patient Information"
              className="ap-fade ap-fade-1"
            >
              <Row gutter={[20, 0]}>
                <Col xs={24}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter full name!" }]}
                  >
                    <Input
                      placeholder="Enter full name"
                      prefix={<UserOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter email!" },
                      { type: "email", message: "Enter a valid email!" },
                    ]}
                  >
                    <Input
                      placeholder="patient@example.com"
                      prefix={<MailOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[{ required: true, message: "Please enter phone number!" }]}
                  >
                    <Input
                      placeholder="Enter phone number"
                      prefix={<PhoneOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Location"
                    name="location"
                    rules={[{ required: true, message: "Please select a location!" }]}
                  >
                    <Select
                      placeholder="Select a location"
                      suffixIcon={<EnvironmentOutlined style={{ color: MUTED }} />}
                    >
                      {locations.map((l) => (
                        <Option key={l._id} value={l._id}>
                          {l.location}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      { required: true, message: "Please enter a password!" },
                      {
                        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                        message:
                          "Min 8 chars with uppercase, lowercase, number & special character",
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder="Enter password"
                      prefix={<LockOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            {/* ── Section 2: Account Status ── */}
            <Section
              icon={<SafetyOutlined />}
              title="Account Status"
              className="ap-fade ap-fade-2"
            >
              <div className="ap-hint">
                <div className="ap-hint-dot" />
                <Text style={{ color: EMERALD, fontSize: 12, lineHeight: 1.5 }}>
                  Patients created via the admin portal are automatically verified and can
                  log in immediately — no OTP step required.
                </Text>
              </div>
            </Section>

            {/* ── Submit ── */}
            <div className="ap-fade ap-fade-3">
              <Button
                className="ap-submit"
                htmlType="submit"
                icon={<UserAddOutlined />}
              >
                Create Patient Account
              </Button>
            </div>

          </Form>
        </div>
      </div>
    </Layout>
  );
}

export default AddPatient;